import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Spin,
  Row,
  Col,
  Popconfirm,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  UploadOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';

const { TextArea } = Input;

const MAX_IMAGENES = 5;
const MAX_DIMENSION = 1920; // px en el lado más largo (mantiene relación de aspecto)
const JPEG_QUALITY = 0.92;  // alta calidad, pérdida casi imperceptible

/**
 * Comprime la imagen: redimensiona a max MAX_DIMENSION y exporta JPEG de alta calidad.
 * Reduce peso sin pérdida visible de calidad.
 */
function comprimirImagen(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
          if (w >= h) {
            h = Math.round((h * MAX_DIMENSION) / w);
            w = MAX_DIMENSION;
          } else {
            w = Math.round((w * MAX_DIMENSION) / h);
            h = MAX_DIMENSION;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Error al comprimir'));
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result;
              if (typeof result === 'string' && result.startsWith('data:')) {
                const base64 = result.split(',')[1];
                resolve({
                  base64,
                  tipo: 'image/jpeg',
                  preview: result,
                });
              } else {
                reject(new Error('Error al leer imagen'));
              }
            };
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error al cargar la imagen'));
    };
    img.src = url;
  });
}

const DifusionesClub = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [imagenes, setImagenes] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const fileInputRef = useRef(null);

  const loadDifusiones = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/club/difusiones');
      if (response.ok) {
        const data = await response.json();
        setList(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar difusiones');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar difusiones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDifusiones();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setImagenes([]);
    setModalVisible(true);
  };

  const openEdit = (item) => {
    setEditingId(item.dif_id);
    form.setFieldsValue({
      dif_descripcion: item.dif_descripcion ?? '',
    });
    const arr = (item.imagenes || []).map((img, i) => ({
      key: `edit-${item.dif_id}-${i}-${Date.now()}`,
      preview: img.dif_imagen_base64
        ? `data:${img.dif_imagen_tipo || 'image/jpeg'};base64,${img.dif_imagen_base64}`
        : null,
      base64: img.dif_imagen_base64 || null,
      tipo: img.dif_imagen_tipo || 'image/jpeg',
    }));
    setImagenes(arr);
    setModalVisible(true);
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.type.startsWith('image/')) {
      message.warning('Elegí una imagen (JPG, PNG, etc.)');
      return;
    }
    if (imagenes.length >= MAX_IMAGENES) {
      message.warning(`Máximo ${MAX_IMAGENES} imágenes`);
      return;
    }
    try {
      const { base64, tipo, preview } = await comprimirImagen(file);
      setImagenes((prev) => [
        ...prev,
        { key: `new-${Date.now()}`, preview, base64, tipo },
      ]);
    } catch (err) {
      console.error('Error al comprimir imagen:', err);
      message.error('No se pudo procesar la imagen. Probá con otra.');
    }
  };

  const quitarImagen = (index) => {
    setImagenes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
    e.target.style.opacity = '0.5';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = Number(e.dataTransfer.getData('text/plain'));
    if (fromIndex === dropIndex) return;
    setImagenes((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(dropIndex, 0, removed);
      return next;
    });
    setDragIndex(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const body = {
        dif_descripcion: values.dif_descripcion,
        imagenes: imagenes.map((img) => ({ base64: img.base64, tipo: img.tipo })),
      };
      if (editingId) {
        const response = await apiClient.put(`/club/difusiones/${editingId}`, body);
        if (response.ok) {
          message.success('Difusión actualizada');
          setModalVisible(false);
          loadDifusiones();
        } else {
          const err = await response.json();
          message.error(err.message || 'Error al actualizar');
        }
      } else {
        const response = await apiClient.post('/club/difusiones', body);
        if (response.ok) {
          message.success('Difusión creada');
          setModalVisible(false);
          loadDifusiones();
        } else {
          const err = await response.json();
          message.error(err.message || 'Error al crear');
        }
      }
    } catch (err) {
      if (err.errorFields) return;
      message.error('Completá los datos');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/club/difusiones/${id}`);
      if (response.ok) {
        message.success('Difusión eliminada');
        loadDifusiones();
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al eliminar');
      }
    } catch (error) {
      message.error('Error al eliminar');
    }
  };

  const firstImage = (item) => {
    const imgs = item.imagenes || [];
    if (imgs.length === 0) return null;
    const first = imgs[0];
    if (!first.dif_imagen_base64) return null;
    return `data:${first.dif_imagen_tipo || 'image/jpeg'};base64,${first.dif_imagen_base64}`;
  };

  return (
    <MainLayout>
      <Card
        title="Difusiones"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nueva difusión
          </Button>
        }
      >
        <Spin spinning={loading}>
          {list.length === 0 && !loading ? (
            <Empty description="No hay difusiones. Creá una para mostrar imagen y descripción en el inicio público." />
          ) : (
            <Row gutter={[16, 16]}>
              {list.map((item) => (
                <Col xs={24} sm={12} md={8} key={item.dif_id}>
                  <Card
                    size="small"
                    cover={
                      firstImage(item) ? (
                        <div
                          style={{
                            background: '#1a1a1a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 120,
                            maxHeight: 300,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={firstImage(item)}
                            alt="Difusión"
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 300,
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            height: 150,
                            background: '#2d2d2d',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PictureOutlined style={{ fontSize: 48, color: '#666' }} />
                        </div>
                      )
                    }
                    actions={[
                      <Button
                        type="text"
                        key="edit"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(item)}
                      >
                        Editar
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title="¿Eliminar esta difusión?"
                        onConfirm={() => handleDelete(item.dif_id)}
                        okText="Sí"
                        cancelText="No"
                      >
                        <Button type="text" danger icon={<DeleteOutlined />}>
                          Eliminar
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <Card.Meta
                      description={
                        item.dif_descripcion
                          ? (item.dif_descripcion.length > 100
                              ? item.dif_descripcion.substring(0, 100) + '…'
                              : item.dif_descripcion)
                          : 'Sin descripción'
                      }
                    />
                    {(item.imagenes || []).length > 1 && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#b0b0b0' }}>
                        {(item.imagenes || []).length} imágenes
                      </div>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Spin>
      </Card>

      <Modal
        title={editingId ? 'Editar difusión' : 'Nueva difusión'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        okText={editingId ? 'Guardar' : 'Crear'}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="dif_descripcion"
            label="Descripción (máx. 1000 caracteres)"
            rules={[
              { required: true, message: 'Escribí una descripción' },
              { max: 1000, message: 'Máximo 1000 caracteres' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Descripción de la difusión"
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={`Imágenes (opcional, hasta ${MAX_IMAGENES}). Arrastrá para ordenar.`}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {imagenes.map((img, index) => (
                <div
                  key={img.key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 8,
                    background: dragIndex === index ? '#404040' : '#2d2d2d',
                    borderRadius: 8,
                    border: '1px solid #404040',
                    cursor: 'grab',
                  }}
                >
                  <HolderOutlined style={{ color: '#b0b0b0', cursor: 'grab' }} />
                  <img
                    src={img.preview}
                    alt={`Imagen ${index + 1}`}
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: 'cover',
                      borderRadius: 4,
                      border: '1px solid #404040',
                    }}
                  />
                  <span style={{ flex: 1, color: '#b0b0b0', fontSize: 12 }}>
                    Imagen {index + 1}
                  </span>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => quitarImagen(index)}
                  >
                    Quitar
                  </Button>
                </div>
              ))}
              {imagenes.length < MAX_IMAGENES && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Agregar imagen ({imagenes.length}/{MAX_IMAGENES})
                  </Button>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#b0b0b0' }}>
                    Se comprimen en alta calidad (máx. 1920px)
                  </span>
                </div>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default DifusionesClub;
